import { Box, Flex, Text, Select } from "@chakra-ui/react";
import { DataTable } from "@root/components";
import { DELETE, EDIT, PIN } from "@root/constants";
import { useEffect, useState, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CustomButton } from "@root/components/common/button/customButton";
import { AddIcon, ChevronDownIcon } from "@chakra-ui/icons";
import { SuperAdminPlaceholderIcon } from "@root/assets/icons/SuperAdminPlaceholderIcon";
import { ModalDelete } from "@root/components/modal-delete";
import { TableColumns } from "./TableColumns";
import { useNoticeContext } from "@root/context/notice-context";
import AddNoticeModal from "../add-notice-modal/AddNoticeModal";
import SearchInput from "../common/SearchInput";
import { useNoticeTypeContext } from "@root/context/notice-type-context";
import CustomSelect from "@root/components/common/CustomSelect";

export const AdminNotice = () => {
  const {
    noticeData,
    pageNo,
    setPageNo,
    pageCount,
    doUpdateNotice,
    removeNotice,
    fetchNotice,
    addNotice,
    pinNotice,
    addNoticeLoading,
  } = useNoticeContext();

  const [selectedRow, setSelectedRow] = useState<any>(null);
  const [isAddNoticeOpen, setIsAddNoticeOpen] = useState(false);
  const [isDeleteModal, setIsDeleteModal] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const { t } = useTranslation("common");
  const { noticeTypeData, fetchNoticeType } = useNoticeTypeContext();
  const [selectedNoticeType, setSelectedNoticeType] = useState("");

  useEffect(() => {
    fetchNoticeType({});
  }, []);

  function handleActions(type: string, rowData: any) {
    if (type === EDIT) {
      console.log(rowData);
      setSelectedRow(rowData);
      handleOpenAddNotice();
    } else if (type === DELETE) {
      setSelectedRow(rowData);
      setIsDeleteModal(true);
    } else if (type === PIN) {
      pinNotice(rowData.id, rowData.pin);
    }
  }

  function handlePageChange(newPage: number) {
    setPageNo(newPage);
  }

  function handleOpenAddNotice() {
    setIsAddNoticeOpen(true);
  }

  const handleCloseAddNotice = useCallback(() => {
    setIsAddNoticeOpen(false);
    setSelectedRow(null);
    setHasSubmitted(false);
  }, []);

  const addNewNotice = async (values: any) => {
    const formData = new FormData();

    formData.append("title", values?.title || "");
    formData.append("description", values?.description || "");
    formData.append("recipientType", String(values?.recipientType || 0));
    formData.append("noticeTypeId", String(values?.noticeTypeId));
    formData.append("createdById", String(values?.createdById || 0));
    formData.append("receiverId", String(values?.receiverId || 0));
    formData.append("isPublish", String(values?.isPublish ?? true));
    formData.append(
      "publishedAt",
      values?.publishedAt || new Date().toISOString()
    );

    // Append files - this is the key part!
    if (values?.Documents && values.Documents.length > 0) {
      values.Documents.forEach((file: File) => {
        formData.append("documents", file); // Or 'Documents[]' depending on your backend
      });
    }

    setHasSubmitted(true);
    await addNotice(formData);
  };

  useEffect(() => {
    if (!addNoticeLoading && addNoticeLoading !== undefined && hasSubmitted) {
      handleCloseAddNotice();
      setHasSubmitted(false);
    }
  }, [addNoticeLoading, hasSubmitted, handleCloseAddNotice]);

  const editNotice = async (values: any) => {
    const formData = new FormData();

    formData.append("title", values?.title || "");
    formData.append("description", values?.description || "");
    formData.append("recipientType", String(values?.recipientType || 0));
    formData.append("noticeTypeId", String(values?.noticeTypeId));
    formData.append("createdById", String(values?.createdById || 0));
    formData.append("receiverId", String(values?.receiverId || 0));
    formData.append("isPublish", String(values?.isPublish ?? true));
    formData.append(
      "publishedAt",
      values?.publishedAt || new Date().toISOString()
    );

    // Append files - only File instances (not existing document objects)
    if (values?.Documents && values.Documents.length > 0) {
      values.Documents.forEach((doc: any) => {
        if (doc instanceof File) {
          formData.append("documents", doc);
        }
      });
    }

    setHasSubmitted(true);
    await doUpdateNotice(formData, selectedRow?.id);
    handleCloseAddNotice();
  };

  const removeNoticeFunc = async () => {
    if (selectedRow) {
      await removeNotice(selectedRow.id);
      setIsDeleteModal(false);
      setSelectedRow(null);
    }
  };
  const isInitialMount = useRef(true);
  const lastFetchRef = useRef<string>("");

  // Initial fetch on mount
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      fetchNotice();
      return;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch notices when page or search changes (skip initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      return;
    }

    // Create a unique key for this fetch to prevent duplicate calls
    const fetchKey = `${searchValue}-${pageNo}`;
    if (lastFetchRef.current !== fetchKey) {
      lastFetchRef.current = fetchKey;
      fetchNotice(searchValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, searchValue]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    setPageNo(1); // Reset to page 1 when searching - this will trigger the useEffect
  };

  const handleNoticeTypeChange = (value: string) => {
    setSelectedNoticeType(value);
  };
  const filteredNoticeData = selectedNoticeType
    ? noticeData?.filter(
        (notice: any) => String(notice.noticeTypeId) === selectedNoticeType
      )
    : noticeData;

  const renderDataTable = () => {
    // Use pageCount if available, otherwise fallback to filtered data length
    // pageCount should be the total number of items (not pages)
    const totalItems =
      pageCount > 0 ? pageCount : filteredNoticeData?.length || 0;

    return (
      <Box>
        <DataTable
          columns={TableColumns(handleActions, t)}
          data={filteredNoticeData}
          showPagination={true}
          page={pageNo}
          theadBg="#f3f1ff"
          pageCount={totalItems}
          pageSize={10}
          handlePageChange={handlePageChange}
        />
      </Box>
    );
  };
  const isEmpty = !filteredNoticeData || filteredNoticeData.length === 0;

  const renderNoticeType = () => {
    if (!noticeTypeData?.length) return null;
    return noticeTypeData.map((item: any) => (
      <option key={item?.id} value={item?.id}>
        {item?.name}
      </option>
    ));
  };

  return (
    <Box
      py={4}
      px={8}
      bg="#fafbfc"
      minW={"full"}
      minH="100vh"
      display="flex"
      flexDirection="column"
      gap={4}
    >
      <Flex justifyContent={"space-between"} mt={"16px"}>
        <Box>
          <Text fontSize={"24px"} fontWeight={600} color={"#2F2F34"}>
            {t("notice.manageNotices")}
          </Text>
          <Text mt={"8px"} fontSize={"16px"} fontWeight={400} color={"#798692"}>
            {t("notice.manageAndAddNotices")}
          </Text>
        </Box>
        <CustomButton
          leftIcon={<AddIcon color="#fff" />}
          text={t("notice.addNewNotice")}
          colorScheme="purple"
          borderRadius={"8px"}
          width="fit-content"
          fontSize="14px"
          fontWeight={500}
          height={"48px"}
          onClick={handleOpenAddNotice}
          background="#1819B4"
          _hover={{ background: "#1819B4" }}
        />
      </Flex>
      <Flex>
        {" "}
        <SearchInput
          maxWidth="600px"
          value={searchValue}
          onChange={handleSearch}
        />{" "}
        <CustomSelect
          placeholder={t("userStatus.all")}
          value={selectedNoticeType}
          onChange={handleNoticeTypeChange}
          borderRadius={"62px"}
          color={"#5C6670"}
          border={"1px solid #5C6670"}
          options={
            noticeTypeData?.map((item: any) => ({
              value: String(item?.id),
              label: item?.name,
            })) || []
          }
        />
      </Flex>

      {isEmpty ? (
        <Flex
          flexDir={"column"}
          alignItems="center"
          justifyContent="center"
          flex={1}
          minH="400px"
        >
          <SuperAdminPlaceholderIcon />
          <Text fontSize={"20px"} fontWeight={600} color={"#000000"}>
            {t("notice.noNoticeYet")}
          </Text>
          <Text
            mb={"16px"}
            mt={"8px"}
            fontSize={"16px"}
            fontWeight={400}
            color={"#000000"}
          >
            {t("notice.addNoticeHint")}
          </Text>
        </Flex>
      ) : (
        <Box maxH={"calc(100vh - 180px)"} overflowY={"auto"}>
          {renderDataTable()}
        </Box>
      )}

      <AddNoticeModal
        isOpen={isAddNoticeOpen}
        onClose={handleCloseAddNotice}
        initialValues={selectedRow}
        onSubmit={selectedRow ? editNotice : addNewNotice}
        addNoticeLoading={addNoticeLoading}
      />

      <ModalDelete
        isOpen={isDeleteModal}
        onClose={() => setIsDeleteModal(false)}
        onConfirm={removeNoticeFunc}
        title={t("notice.confirmDelete")}
        description={t("notice.confirmDeleteDescription")}
        deleteButtonTitle={t("notice.delete")}
        cancelButtonTitle={t("notice.cancel")}
      />
    </Box>
  );
};
